import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../services';
import type {
  AlertKind, AlertStatus, ClosureInput, ConsentsUpdate, ForecastTarget, LocalEventInput, LocationSettingsPatch,
  MarketingInput, TenantSettingsPatch, UserInput,
} from './types';

export const qk = {
  location: (id: string) => ['location', id] as const,
  forecast: (id: string, target: ForecastTarget, days: number) => ['forecast', id, target, days] as const,
  hourly: (id: string, days: number) => ['hourly', id, days] as const,
  channels: (id: string, days: number) => ['channels', id, days] as const,
  products: (id: string, days: number) => ['products', id, days] as const,
  group: (days: number) => ['group', days] as const,
  alerts: (id: string, status?: AlertStatus, kind?: AlertKind) => ['alerts', id, status ?? 'all', kind ?? 'all'] as const,
  accuracy: (id: string, window: number) => ['accuracy', id, window] as const,
  menu: (id: string) => ['menu', id] as const,
  events: (id: string) => ['events', id] as const,
  marketing: (id: string) => ['marketing', id] as const,
  closures: (id: string) => ['closures', id] as const,
  influencers: (id: string) => ['influencers', id] as const,
  tenantSettings: ['tenant-settings'] as const,
  consents: ['consents'] as const,
  benchmarks: (id: string) => ['benchmarks', id] as const,
  users: ['users'] as const,
};

export function useLocation(id: string) {
  const api = useApi();
  return useQuery({ queryKey: qk.location(id), queryFn: () => api.getLocation(id) });
}
export function useForecast(id: string, target: ForecastTarget, days: number) {
  const api = useApi();
  return useQuery({ queryKey: qk.forecast(id, target, days), queryFn: () => api.getForecast(id, target, days) });
}
export function useDailyMonth(id: string, month?: string) {
  const api = useApi();
  return useQuery({ queryKey: ['daily-month', id, month ?? 'default'], queryFn: () => api.getDailyMonth(id, month) });
}
export function useDayDetail(id: string, day: string | null) {
  const api = useApi();
  return useQuery({ queryKey: ['daily-day', id, day], queryFn: () => api.getDayDetail(id, day!), enabled: Boolean(day) });
}
export function useHourly(id: string, days = 7) {
  const api = useApi();
  return useQuery({ queryKey: qk.hourly(id, days), queryFn: () => api.getHourlyForecast(id, days) });
}
export function useChannels(id: string, days = 7) {
  const api = useApi();
  return useQuery({ queryKey: qk.channels(id, days), queryFn: () => api.getChannelForecast(id, days) });
}
export function useProducts(id: string, days = 7) {
  const api = useApi();
  return useQuery({ queryKey: qk.products(id, days), queryFn: () => api.getProductForecast(id, days) });
}
export function useGroupForecast(days = 7, enabled = true) {
  const api = useApi();
  return useQuery({ queryKey: qk.group(days), queryFn: () => api.getGroupForecast(days), enabled });
}
export function useAlerts(id: string, status?: AlertStatus, kind?: AlertKind) {
  const api = useApi();
  return useQuery({ queryKey: qk.alerts(id, status, kind), queryFn: () => api.getAlerts(id, status, kind) });
}
export function useUpdateAlert() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'dismissed' | 'done' }) => api.updateAlert(id, status),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['alerts'] }),
  });
}
export function useAccuracy(id: string, window = 14) {
  const api = useApi();
  return useQuery({ queryKey: qk.accuracy(id, window), queryFn: () => api.getAccuracy(id, window) });
}
export function useMenu(id: string) {
  const api = useApi();
  return useQuery({ queryKey: qk.menu(id), queryFn: () => api.getMenu(id) });
}

export function useEvents(id: string) {
  const api = useApi();
  return useQuery({ queryKey: qk.events(id), queryFn: () => api.listEvents(id) });
}
export function useMarketing(id: string) {
  const api = useApi();
  return useQuery({ queryKey: qk.marketing(id), queryFn: () => api.listMarketing(id) });
}
export function useClosures(id: string) {
  const api = useApi();
  return useQuery({ queryKey: qk.closures(id), queryFn: () => api.listClosures(id) });
}

/** Inputs change forecasts, so invalidate those too. */
function useInputMutation<TArgs>(fn: (args: TArgs) => Promise<unknown>, keys: string[]) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => keys.forEach((k) => qc.invalidateQueries({ queryKey: [k] })),
  });
}
export function useCreateEvent(id: string) {
  const api = useApi();
  return useInputMutation((input: LocalEventInput) => api.createEvent(id, input), ['events']);
}
export function useDeleteEvent() {
  const api = useApi();
  return useInputMutation((eventId: string) => api.deleteEvent(eventId), ['events']);
}
export function useCreateMarketing(id: string) {
  const api = useApi();
  return useInputMutation((input: MarketingInput) => api.createMarketing(id, input), ['marketing', 'influencers']);
}
export function useDeleteMarketing() {
  const api = useApi();
  return useInputMutation((activityId: string) => api.deleteMarketing(activityId), ['marketing', 'influencers']);
}
export function useCreateClosure(id: string) {
  const api = useApi();
  return useInputMutation((input: ClosureInput) => api.createClosure(id, input), ['closures']);
}
export function useDeleteClosure() {
  const api = useApi();
  return useInputMutation((closureId: string) => api.deleteClosure(closureId), ['closures']);
}

export function useInfluencers(id: string) {
  const api = useApi();
  return useQuery({ queryKey: qk.influencers(id), queryFn: () => api.getInfluencers(id) });
}

export function usePatchLocationSettings(id: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: LocationSettingsPatch) => api.patchLocationSettings(id, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.location(id) });
      qc.invalidateQueries({ queryKey: ['hourly', id] });
    },
  });
}
export function useTenantSettings(enabled = true) {
  const api = useApi();
  return useQuery({ queryKey: qk.tenantSettings, queryFn: () => api.getTenantSettings(), enabled });
}
export function usePatchTenantSettings() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: TenantSettingsPatch) => api.patchTenantSettings(patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.tenantSettings });
      qc.invalidateQueries({ queryKey: ['location'] });
    },
  });
}
export function useConsents() {
  const api = useApi();
  return useQuery({ queryKey: qk.consents, queryFn: () => api.getConsents() });
}
export function usePutConsents() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (u: ConsentsUpdate) => api.putConsents(u),
    onSuccess: (data) => qc.setQueryData(qk.consents, data),
  });
}
export function useBenchmarks(id: string) {
  const api = useApi();
  return useQuery({ queryKey: qk.benchmarks(id), queryFn: () => api.getBenchmarks(id) });
}

export function useUsers(enabled = true) {
  const api = useApi();
  return useQuery({ queryKey: qk.users, queryFn: () => api.listUsers(), enabled });
}
export function useCreateUser() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({ mutationFn: (u: UserInput) => api.createUser(u), onSuccess: () => qc.invalidateQueries({ queryKey: qk.users }) });
}
export function useUpdateUser() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<UserInput> }) => api.updateUser(id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.users }),
  });
}
export function useDeleteUser() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => api.deleteUser(id), onSuccess: () => qc.invalidateQueries({ queryKey: qk.users }) });
}
